import {
  type OptionsType,
  type Score,
  zxcvbn,
  zxcvbnOptions,
} from "@zxcvbn-ts/core";
import clsx from "clsx";
import { times } from "lodash-es";
import { motion, type Variants } from "motion/react";
import { use } from "react";

const messageForScore: Record<Score, string> = {
  4: "Looks great!",
  3: "Good enough",
  2: "Could be better",
  1: "Easy to guess",
  0: "Terrible",
};

const variants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
  },
};

const PasswordStrength: React.FC<{
  value: string;
  promise: Promise<OptionsType>;
}> = ({ value, promise }) => {
  const options = use(promise);
  zxcvbnOptions.setOptions(options);
  const { score, feedback } = zxcvbn(value);
  return (
    <motion.div
      className="px-1"
      initial="hidden"
      animate="visible"
      transition={{
        staggerChildren: 1 / 10,
      }}
    >
      <motion.h3 variants={variants} className="mb-2 font-title">
        <span className="font-medium">Password strength:</span>{" "}
        {messageForScore[score]}
      </motion.h3>
      <motion.div
        variants={variants}
        className={clsx("mb-4 grid h-1 grid-cols-4 gap-1", {
          "text-success": score >= 3,
          "text-warning": score === 2,
          "text-error": score === 1,
        })}
      >
        {times(4, (index) => (
          <div
            key={index}
            className={clsx(
              "h-full rounded-full",
              score > index ? "bg-current" : "bg-base-300",
            )}
            data-active={index >= score}
          />
        ))}
      </motion.div>
      <motion.div variants={variants} className="text-sm">
        {score >= 3 ? (
          <>
            Your password is{" "}
            {score === 4 ? "pretty solid." : "not easily guessable."}{" "}
            {times(score - 2, () => "👍")}
          </>
        ) : (
          <ul className="list-inside list-disc space-y-1.5">
            {feedback.warning && <li>{feedback.warning}</li>}
            {feedback.suggestions.map((suggestion, index) => (
              <li key={index}>{suggestion}</li>
            ))}
          </ul>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PasswordStrength;
